import moment from "moment";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getAuthorizedUser } from "src/actions/auth";
import { clearUserDetails, manageUserStatus } from "src/actions/user";
import SelectMenu from "src/components/Shared/Forms/Selects/SelectMenu";
import QuickMenu from "src/components/Shared/QuickMenu";
import TableRow from "src/components/Shared/Table/TableRow";
import { authorizeUserComponentAccess } from "src/helpers/authorizeUserComponentAccess";

import UserProfile from "src/components/Users/UserProfile";
import { classNames } from "src/helpers/classNames";

const UserEntry = ({ user, clearUserDetails, manageUserStatus, getPages, setDeleteId, userAuth, site, getAuthorizedUser, refreshUsers = () => {}, manageUserRole = () => {}, workspaceDetails = {}, ...props }) => {
  const navigate = useNavigate();

  const formatMemberDate = (date) => {
    if (!date) return "";
    return moment(new Date(date)).format("MM/DD/YYYY");
  };

  const roles = [
    { key: "Participant", value: "participant" },
    { key: "Editor", value: "editor" },
    { key: "Privileged", value: "privileged" },
    { key: "Admin", value: "admin" },
  ];

  const [activeStatus, setActiveStatus] = useState(true);

  useEffect(() => {
    setActiveStatus(user.active_status);
  }, [user.active_status]);

  const insertTypes = {
    SSO_LOGIN: "SSO",
    MANUAL_INPUT: "Manually",
    BULK_UPLOAD: "Bulk",
    DOMO_DATASET: "Dataset",
  };

  const setPreviewUserId = async (id) => {
    localStorage.setItem("impersonatedUserId", id);
    const ac = new AbortController();
    await getAuthorizedUser(ac.signal);
    navigate("/workspaces/menu");
  };

  return (
    <TableRow keyId={user?._id}>
      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-6">
        <div className="flex items-center">
          <div className="hidden h-10 w-10 flex-shrink-0 overflow-hidden rounded-full sm:block">{<UserProfile user={user || {}} />}</div>
          <div className="sm:ml-4">
            <div className="flex items-center gap-x-2 font-medium text-gray-900">
              {authorizeUserComponentAccess(userAuth, workspaceDetails?._id, "user", ["update"]) ? (
                <Link
                  to={window.location.pathname.includes("/admins") ? `/admins/${user._id}` : `/users/${user._id}`}
                  className="text-gray-900 font-medium cursor-pointer hover:underline">
                  {user?.name}
                </Link>
              ) : (
                user?.name
              )}
              {user?.default_user && <span className="rounded-full bg-gray-100 px-2 py-[1px] text-gray-400">Admin</span>}
            </div>

            <div className="text-gray-500">
              {insertTypes[user.creation_type]} created {formatMemberDate(user.created_at)}
            </div>
          </div>
        </div>
      </td>
      <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500 sm:table-cell">
        <div className="text-gray-900">{user?.email}</div>
        <div className="text-gray-500">{user?.username}</div>
      </td>
      <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500 lg:table-cell">
        <button
          onClick={async () => {
            await manageUserStatus(user);
            refreshUsers(user);
            setActiveStatus(!activeStatus);
          }}
          className={classNames("flex w-[52px] justify-center rounded-full px-2 text-xs font-semibold leading-5", activeStatus ? "bg-green-200 text-green-700 opacity-50" : "bg-gray-50 text-gray-500")}>
          {activeStatus ? "Active" : "Inactive"}
        </button>
      </td>
      {workspaceDetails?.workspace_type === "JWT_FULL_EMBED" && (
        <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500 lg:table-cell">
          <SelectMenu
            options={roles}
            setOption={(e) => manageUserRole(e, user)}
            startIndex={roles.findIndex((rl) => rl.value === user.type)}
          />
        </td>
      )}
      <td className="relative flex justify-end whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium sm:pr-6">
        <QuickMenu
          items={[
            {
              name: "View Profile",
              hideWhen: !authorizeUserComponentAccess(userAuth, workspaceDetails?._id, "user", ["update"]),
              onClick: () => {
                clearUserDetails(null);
                navigate(window.location.pathname.includes("/admins") ? `/admins/${user._id}?tab=View+Profile` : `/users/${user._id}?tab=View+Profile`);
              },
            },
            {
              name: "Workspaces",
              onClick: () => {
                clearUserDetails(null);
                navigate(window.location.pathname.includes("/admins") ? `/admins/${user._id}?tab=Workspaces` : `/users/${user._id}?tab=Workspaces`);
              },
            },
            {
              name: "Permissions",
              hideWhen: window.location.pathname.includes("/admins"),
              onClick: () => {
                clearUserDetails(null);
                navigate(window.location.pathname.includes("/admins") ? `/admins/${user._id}?tab=Permissions` : `/users/${user._id}?tab=Permissions`);
              },
            },
            {
              type: "hr",
            },
            {
              name: "Impersonate",
              hideWhen: window.location.pathname.includes("/admins"),
              isBeta: true,
              onClick: () => {
                setPreviewUserId(user._id);
              },
            },
            {
              name: "Delete",
              hideWhen: user.default_user || site.api_state === "DOMO_DATASET" || !authorizeUserComponentAccess(userAuth, workspaceDetails?._id, "user", ["delete"]) || (userAuth?.type !== "admin" && user?.type === "admin"),
              onClick: () => {
                setDeleteId(user._id);
              },
              color: "text-red-500",
            },
          ]}
        />
      </td>
    </TableRow>
  );
};

const mapStateToProps = (state) => {
  return {
    userAuth: state.auth.user,
    site: state.site,
  };
};

export default connect(mapStateToProps, { clearUserDetails, manageUserStatus, getAuthorizedUser })(UserEntry);
